import { ReactNode } from 'react';
import GoogleIcon from '@mui/icons-material/Google';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { InputAdornment } from '@mui/material';
import logo from '../../../images/klydo-studio-logo.svg';
import { KLYDO_WHITE, KLYDO_YELLOW } from '../../../consts/colors';
import DSBox from '../../../design-system/DSBox';
import DSIconButton from '../../../design-system/DSIconButton';
import { DSTypography } from '../../../design-system/Typography';
import DSTextField from '../../../design-system/DSTextField';
import DSDivider from '../../../design-system/DSDivider';
import DSTabs from '../../../design-system/DSTabs';
import DSTab from '../../../design-system/DSTab';
import DSPrimaryButton from '../../../design-system/DSPrimaryButton';
import useAuthPage from '../hooks/useAuthPage';

type Props = {
  /** Auth state/handlers, owned by the page so a single instance survives layout switches. */
  auth: ReturnType<typeof useAuthPage>;
  /** Optional content rendered between the logo and the tabs (e.g. campaign headline on mobile). */
  headline?: ReactNode;
  logoWidth?: number;
};

const AuthForm = ({ auth, headline, logoWidth = 200 }: Props) => {
  const {
    isRegister,
    email,
    password,
    setPassword,
    handlePrimaryButtonPress,
    isSubmitting,
    handleForgotPassword,
    handleGoogleSignIn,
    handleTabChange,
    setEmail,
    handleVerifyEmail,
    handleEmailBlur,
    handlePasswordBlur,
    showPassword,
    togglePasswordVisibility,
    resendDisabled,
  } = auth;

  return (
    <DSBox
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        handlePrimaryButtonPress();
      }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        width: '100%',
      }}
    >
      <DSBox sx={{ textAlign: 'center', mb: headline ? 1 : 3 }}>
        <img src={logo} alt="Klydo Clock Logo" style={{ width: logoWidth }} />
      </DSBox>

      {headline}

      <DSTabs
        value={isRegister}
        onChange={handleTabChange}
        sx={{
          marginBottom: 2,
          '.MuiTabs-flexContainer': {
            borderBottom: '1px solid #555',
          },
          '.MuiTab-root': {
            color: '#888',
            '&.Mui-selected': {
              color: KLYDO_YELLOW,
              fontWeight: 'bold',
            },
          },
        }}
      >
        <DSTab label="Login" value={false} />
        <DSTab label="Register" value={true} />
      </DSTabs>

      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
          maxWidth: 400,
        }}
      >
        <DSTextField
          label="Email"
          variant="outlined"
          type="email"
          name="email"
          autoComplete="email"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={handleEmailBlur}
          InputProps={{
            style: { color: KLYDO_WHITE },
          }}
          InputLabelProps={{
            style: { color: KLYDO_WHITE, opacity: 0.6 },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: KLYDO_WHITE, opacity: 0.4 },
              '&:hover fieldset': { borderColor: KLYDO_YELLOW, opacity: 1 },
              '&.Mui-focused fieldset': {
                borderColor: KLYDO_YELLOW,
                opacity: 1,
              },
            },
          }}
        />

        <DSTextField
          label="Password"
          variant="outlined"
          type={showPassword ? 'text' : 'password'}
          name="password"
          autoComplete={isRegister ? 'new-password' : 'current-password'}
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onBlur={handlePasswordBlur}
          InputProps={{
            style: { color: KLYDO_WHITE },
            endAdornment: (
              <InputAdornment position="end">
                <DSIconButton
                  onClick={togglePasswordVisibility}
                  edge="end"
                  sx={{
                    color: KLYDO_WHITE,
                    opacity: 0.6,
                    '&:hover': {
                      color: KLYDO_YELLOW,
                      opacity: 1,
                    },
                  }}
                >
                  {showPassword ? (
                    <VisibilityOff sx={{ fontSize: 18 }} />
                  ) : (
                    <Visibility sx={{ fontSize: 18 }} />
                  )}
                </DSIconButton>
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            style: { color: KLYDO_WHITE, opacity: 0.6 },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: KLYDO_WHITE, opacity: 0.4 },
              '&:hover fieldset': { borderColor: KLYDO_YELLOW, opacity: 1 },
              '&.Mui-focused fieldset': {
                borderColor: KLYDO_YELLOW,
                opacity: 1,
              },
            },
            '& input::-ms-reveal': { display: 'none' },
            '& input::-ms-clear': { display: 'none' },
            '& input::-webkit-contacts-auto-fill-button': {
              visibility: 'hidden',
            },
            '& input::-webkit-credentials-auto-fill-button': {
              visibility: 'hidden',
            },
          }}
        />

        {!isRegister && (
          <DSTypography
            onClick={handleForgotPassword}
            sx={{
              color: KLYDO_YELLOW,
              textAlign: 'right',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Forgot Password?
          </DSTypography>
        )}
        {isRegister && (
          <DSTypography
            onClick={resendDisabled ? undefined : handleVerifyEmail}
            sx={{
              color: resendDisabled ? KLYDO_WHITE : KLYDO_YELLOW,
              textAlign: 'right',
              cursor: resendDisabled ? 'default' : 'pointer',
              textDecoration: 'underline',
              opacity: resendDisabled ? 0.6 : 1,
              pointerEvents: resendDisabled ? 'none' : 'auto',
            }}
          >
            resend email verification
          </DSTypography>
        )}
      </DSBox>

      <DSPrimaryButton type="submit" disabled={isSubmitting}>
        {isRegister ? 'Join Now' : 'Login'}
      </DSPrimaryButton>

      <DSDivider
        sx={{ my: 2, width: '100%', maxWidth: 400, borderWidth: undefined }}
      >
        <DSTypography sx={{ color: KLYDO_WHITE }}>
          or continue with
        </DSTypography>
      </DSDivider>

      <DSIconButton
        onClick={handleGoogleSignIn}
        sx={{
          backgroundColor: '#fff',
          color: '#000',
          '&:hover': { backgroundColor: '#f5f5f5' },
        }}
      >
        <GoogleIcon />
      </DSIconButton>
    </DSBox>
  );
};

export default AuthForm;
